/**
 * VAL-FILE-SPLIT-1 — split out of ValuationProfileSelect.tsx under
 * docs/FILE_SIZE_POLICY.md. Unchanged from the page: renders one signed (or
 * unsigned) industry profile with its governance record and multiple
 * envelope.
 */
import type { ValuationProfile } from '@/types/valuation'
import { multiple } from '@/utils/format'
import { basisLabel } from './_shared'

const REPO_PATH = /(core\/|_ClaudeCode_Briefs|\.(py|md|yaml)\b)/

export function ProfileCard({
  profile,
  selected,
  onSelect,
}: {
  profile: ValuationProfile
  selected: boolean
  onSelect: () => void
}) {
  const gov = profile.governance
  // Client text only: a citation that is a file in the repo stays in the YAML, never on the card.
  const sources = gov.citations.filter(c => !REPO_PATH.test(c))
  return (
    <button
      type="button"
      onClick={profile.selectable ? onSelect : undefined}
      disabled={!profile.selectable}
      aria-pressed={selected}
      className={`w-full rounded-lg border p-4 text-left transition-colors
        ${!profile.selectable ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60' : 'hover:bg-gray-50'}
        ${selected ? 'border-[#0d1f3c] bg-[#0d1f3c]/5' : 'border-gray-200'}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium text-pal-ink">{profile.display_name}</span>
        <span className="font-mono text-xs text-pal-ink-muted">
          {profile.industry_code} · {profile.version}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        {!profile.selectable && (
          <span className="rounded bg-pal-pill-warning-bg px-2 py-0.5 font-medium text-pal-pill-warning-text">
            {profile.not_selectable_reason ?? `${profile.status} — not signed for use`}
          </span>
        )}
        {profile.is_generic_fallback && (
          <span className="rounded bg-pal-surface-muted px-2 py-0.5 text-pal-ink-soft">
            Signed fallback for unmatched industries
          </span>
        )}
        <span className="text-pal-ink-muted">Premise: {profile.premise_label ?? profile.premise_default}</span>
      </div>

      <dl className="mt-3 space-y-1 text-xs text-pal-ink-soft">
        {profile.multiples.map(m => (
          <div key={m.key} className="flex justify-between gap-4">
            <dt>{m.label ?? m.key}{m.is_tiered ? ` (tiered by ${basisLabel(String(m.tier_basis))})` : ''}</dt>
            <dd>{multiple(m.envelope_low)} – {multiple(m.envelope_high)}</dd>
          </div>
        ))}
      </dl>

      {sources.length > 0 && (
        <p className="mt-1 text-xs text-pal-ink-faint">Sources: {sources.join('; ')}</p>
      )}
    </button>
  )
}
